// Bank matching — spec ch.06: "Source transaction and corresponding invoice,
// amounts, difference, missing-document route … Verified bank/open-item
// adapters, current balances, exact-decimal checks, allocations and external
// reconciliation receipts. No payment initiation."
//
// The v3 layout is kept: the metric tiles, the candidate list, and the two
// cards side by side — bank transaction on the left, the invoice(s) it would
// settle on the right — with the difference between them and the constraints on
// releasing anything.
//
// Four rules make this screen different from the demo's version of it.
//
// **A candidate is evidence, not a recommendation.** ch.02: "Amount alone is
// insufficient: several invoices can share the same total. Surface ambiguity."
// So both sides' versions are shown as observed AND as currently stored, a
// confidence number is printed as the number it is rather than as a verdict,
// and where two candidates in the same list would settle the same invoice the
// screen says so. Nothing here ranks a candidate as "best".
//
// **Nothing is settled until an operation says so.** Approving answers 202 and
// an operation id; the badge then follows GET /operations/{id} through the
// shared ExternalState component — the same one the document workbench uses, so
// there is one implementation of "only a receipt confirms an effect" rather
// than two that could drift. An `unknown` outcome shows a status check and no
// retry control, which at this endpoint is the double-payment guarantee itself.
//
// **A refusal is explained before it happens.** A paused administration, a
// source that moved, a match that is no longer a candidate, a role that may not
// approve: each disables the button WITH its reason, rather than leaving a
// control that answers 409 or 422 when pressed.
//
// **No payment is initiated.** ch.02: "Never create a new payment from a match."
// Reconciliation links a payment the bank already made to an invoice, and the
// screen says so where a reviewer is deciding.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type {
  Accepted,
  Administration,
  BankMatch,
  BankMatches,
  BankMatchingFigures,
  BankStatements,
} from '../api/types.ts';
import { BarChart, CHART_COLOURS } from '../components/Charts.tsx';
import { ExternalState } from '../components/ExternalState.tsx';
import { Icon } from '../components/Icon.tsx';
import { Metric } from '../components/Metric.tsx';
import { filterQuery, useRead } from '../state/useRead.ts';
import { StateBadge } from '../components/StateBadge.tsx';
import * as format from '../format.ts';
import * as money from '../money.ts';
import { MATCH_STATES, display } from '../states.ts';
import { ALL_ADMINISTRATIONS, administrationFilter, useWorkspace, type Workspace } from '../state/workspace.tsx';

/** Roles the API lets approve a match. Anything else is told why, not shown a dead button. */
const MAY_APPROVE = new Set(['owner', 'reviewer']);

interface AdministrationMatches {
  readonly administration: Administration;
  readonly body: BankMatches | null;
  readonly error: string | null;
}

/** Why approval is unavailable, or null when it is available. */
export function blockedReason(
  match: BankMatch,
  constraints: BankMatches['constraints'],
  role: string,
): string | null {
  if (!MAY_APPROVE.has(role)) {
    return `Your role (${role.replace('_', ' ')}) may not approve a bank match.`;
  }
  if (match.status !== 'candidate') {
    return `This match is ${display(MATCH_STATES, match.status).label.toLowerCase()}, so there is nothing to approve.`;
  }
  if (match.sourceChanged) {
    // The server refuses this too — 409, "the candidate was computed against an
    // older bank line". Saying it here means the reviewer learns it before
    // clicking rather than from a conflict.
    return 'The bank line or an invoice changed after this candidate was computed. It needs a fresh candidate.';
  }
  if (constraints.paused) {
    return 'This administration is paused, so no new work may be queued for it.';
  }
  // The capability, which the server rechecks in the transaction that would
  // create the operation. Saying it here is the whole reason the endpoint
  // reports it: a reviewer learns that a match cannot be released BEFORE
  // pressing the button, rather than from an operation that failed afterwards
  // with nothing to undo.
  //
  // Three sentences for three answers, because they call for different things
  // from different people. Null is a fourth and is not "unsupported": it means
  // this build has no connector for the provider, which is a deployment
  // question rather than a fact about the accounting package.
  if (constraints.reconcile === null) {
    return 'This build has no connector for the accounting package this administration is connected to, so nothing can be sent to it.';
  }
  if (constraints.reconcile.support === 'unsupported') {
    return 'The accounting package does not offer bank reconciliation through its API, so this match cannot be sent to it.';
  }
  if (constraints.reconcile.support === 'unverified') {
    // ch.03: "unverified operations are disabled for live writes". Not a
    // refusal by the provider — a refusal by us, because nobody has run this
    // against a real administration.
    return 'Bank reconciliation has not been verified against a real administration for this connector, so it is not available for live writes.';
  }
  if (match.allocations.length === 0) {
    return 'This candidate settles nothing: it has no allocations.';
  }
  return null;
}

/**
 * The difference between what left the bank and what the invoices come to.
 *
 * Exact, in scaled integers, never floats — ch.02 forbids floating-point
 * arithmetic for financial decisions, and a one-cent residual is precisely the
 * thing that separates an exact match from one that stays in review.
 *
 * Undefined when the two sides are in different currencies: a difference
 * between EUR and USD is not a number, and inventing one would hide exactly the
 * ambiguity this screen exists to surface.
 */
export function difference(match: BankMatch): { amount: string; currency: string } | undefined {
  const currency = match.bankLine.amount.currency;
  if (match.allocations.some((allocation) => allocation.amount.currency !== currency)) {
    return undefined;
  }
  try {
    const allocated = money.sum(match.allocations.map((allocation) => allocation.amount.amount));
    return { amount: money.subtract(money.absolute(match.bankLine.amount.amount), allocated), currency };
  } catch {
    // An amount this build cannot represent. Better no number than a wrong one.
    return undefined;
  }
}

/** Invoices that more than one candidate in this list would settle. */
export function contestedOpenItems(matches: readonly BankMatch[]): ReadonlySet<string> {
  const seen = new Map<string, number>();
  for (const match of matches) {
    for (const allocation of match.allocations) {
      seen.set(allocation.openItemId, (seen.get(allocation.openItemId) ?? 0) + 1);
    }
  }
  return new Set([...seen.entries()].filter(([, count]) => count > 1).map(([id]) => id));
}

export interface BankMatchingScreenProps {
  /**
   * Hand a bank line to Customer communication, so the office can ask for the
   * invoice behind it.
   *
   * ch.06 lists a "missing-document route" as part of this screen and ch.02
   * puts the request itself elsewhere: "Missing information creates a request
   * linked to a document/bank line." So this screen does not compose a message
   * — it names the source, and the screen that owns recipients, revisions and
   * approvals takes it from there. A request with no source is a task nobody
   * can close, which is why the subject travels rather than the other way
   * round.
   */
  readonly onAskForDocument?: ((subject: AskSubject) => void) | undefined;
}

/** What travels to Customer communication. Mirrors its RequestSubject. */
export interface AskSubject {
  readonly kind: 'bank_line';
  readonly id: string;
  readonly administrationId: string;
  readonly label: string;
}

export function BankMatchingScreen({ onAskForDocument }: BankMatchingScreenProps = {}): ReactNode {
  const workspace = useWorkspace();
  const [pages, setPages] = useState<readonly AdministrationMatches[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { token, administrations, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;

  const load = useCallback(async () => {
    // The endpoint is per administration. With "all administrations" selected
    // the screen asks for each one the session can see — the same ids GET
    // /administrations returned, so every request is one this session may make.
    const wanted =
      administrationId === ALL_ADMINISTRATIONS
        ? administrations
        : administrations.filter((administration) => administration.id === administrationId);

    const collected: AdministrationMatches[] = [];
    for (const administration of wanted) {
      try {
        const body = await request<BankMatches>(
          tenantPath(tenantId, `/administrations/${encodeURIComponent(administration.id)}/bank-matches`),
          { token },
        );
        collected.push({ administration, body, error: null });
      } catch (caught) {
        // One administration failing must not blank the others: a reviewer
        // with five offices should still see the four that answered.
        collected.push({
          administration,
          body: null,
          error:
            caught instanceof ApiError && caught.isAbsent
              ? 'No longer available to your session.'
              : 'Candidates could not be read.',
        });
      }
    }
    setPages(collected);
  }, [tenantId, token, administrations, administrationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = (pages ?? []).flatMap((page) =>
    (page.body?.matches ?? []).map((match) => ({ match, page })),
  );
  const contested = contestedOpenItems(all.map((entry) => entry.match));
  const selected = all.find((entry) => entry.match.id === selectedId) ?? null;

  const candidates = all.filter((entry) => entry.match.status === 'candidate');
  const changed = all.filter((entry) => entry.match.sourceChanged);
  const filter = administrationFilter(administrationId);
  const figures = useRead<BankMatchingFigures>(`/insights/bank-matching${filterQuery(filter)}`, 'the matching figures');
  const statements = useRead<BankStatements>(`/bank-statements${filterQuery(filter)}`, 'the bank statements');

  return (
    <>
      <div className="heading">
        <h1>Bank matching</h1>
        <p>
          Your AI bookkeeper links payments the bank already made to the invoices they settle. You
          review every one.
        </p>
      </div>

      <div className="metrics">
        <Metric icon="swap" label="Candidates" value={pages === null ? undefined : candidates.length} caption="Waiting for your review" />
        <Metric icon="refresh" label="Source changed" value={pages === null ? undefined : changed.length} caption="Recomputed before they can be approved" />
        <Metric icon="alert" label="Contested invoices" value={pages === null ? undefined : contested.size} caption="More than one candidate would settle them" />
        <Metric
          icon="target"
          label="Approved this month"
          value={figures.data?.approvedThisMonth}
          loading={figures.loading}
          caption={figures.error ?? 'Matches a reviewer approved'}
        />
      </div>

      <div className="workbench">
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Candidates</h2>
              <p>Evidence to judge, not a queue to clear</p>
            </div>
            <span className="badge">{all.length}</span>
          </div>

          {pages === null ? (
            <div className="empty">
              <p>Loading…</p>
            </div>
          ) : all.length === 0 ? (
            <div className="empty">
              <h3>No candidates</h3>
              <p>Nothing is waiting in this selection.</p>
            </div>
          ) : (
            all.map(({ match, page }) => (
              <button
                key={match.id}
                type="button"
                className={`row${match.id === selectedId ? ' selected' : ''}`}
                onClick={() => setSelectedId(match.id)}
              >
                <div className="grow">
                  <strong>{match.bankLine.reference ?? match.bankLine.externalId}</strong>
                  <p>
                    {page.administration.name} · {format.date(match.bankLine.bookedAt)} ·{' '}
                    {money.format(match.bankLine.amount)}
                  </p>
                </div>
                {match.allocations.some((allocation) => contested.has(allocation.openItemId)) ? (
                  <span className="badge warn">Contested</span>
                ) : null}
                <StateBadge table={MATCH_STATES} state={match.status} />
              </button>
            ))
          )}

          {(pages ?? [])
            .filter((page) => page.error !== null)
            .map((page) => (
              <div className="card-foot" key={page.administration.id}>
                {page.administration.name}: {page.error}
              </div>
            ))}
        </div>

        {selected === null || selected.page.body === null ? (
          <div className="card">
            <div className="empty">
              <h3>Select a candidate</h3>
              <p>The transaction, the invoices it would settle and the difference appear here.</p>
            </div>
          </div>
        ) : (
          <MatchDetail
            key={selected.match.id}
            match={selected.match}
            administration={selected.page.administration}
            constraints={selected.page.body.constraints}
            contested={contested}
            workspace={workspace}
            onChanged={() => void load()}
            {...(onAskForDocument === undefined ? {} : { onAskForDocument })}
          />
        )}
      </div>

      <div className="grid21 section-space">
        <StatementsCard read={statements} />
        <WeeksCard read={figures} />
      </div>
    </>
  );
}

const PROVIDER_NAMES: Readonly<Record<string, string>> = { eboekhouden: 'e-Boekhouden' };

/** "wk 43": the ISO week of a Monday, as the design labels its bars. */
function weekLabel(monday: string): string {
  const [y, m, d] = monday.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() + 3);
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  return `wk ${Math.ceil(((thursday.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)}`;
}

const BALANCE_CHECK: Readonly<Record<string, { tone: string; label: string; detail: string }>> = {
  recomputed: {
    tone: 'ok',
    label: 'Balances add up',
    detail: 'Opening balance plus every stored line equals the closing balance, checked again just now.',
  },
  proved_at_import: {
    tone: '',
    label: 'Proved at import',
    detail:
      'Balanced when it was imported. Some of its lines were already delivered by an overlapping export and are stored with that statement, so the sum cannot be repeated here.',
  },
  recomputed_mismatch: {
    tone: 'danger',
    label: 'Balances do not add up',
    detail: 'The stored lines no longer come to the closing balance. Nothing should be approved from this statement until it is explained.',
  },
};

function StatementsCard({
  read,
}: {
  readonly read: { data: BankStatements | null; error: string | null; loading: boolean };
}): ReactNode {
  const statements = read.data?.statements ?? [];
  return (
    <section className="card" aria-labelledby="statements-title">
      <div className="card-head">
        <div>
          <h2 id="statements-title">Latest bank statements</h2>
          <p>MT940 files imported for this selection, and their balance check</p>
        </div>
      </div>
      {read.data === null ? (
        read.error === null ? (
          <div className="empty"><p>Reading…</p></div>
        ) : (
          <p className="card-body muted small" role="status">{read.error}</p>
        )
      ) : statements.length === 0 ? (
        <div className="empty">
          <h3>No statements imported</h3>
          <p>Upload an MT940 file on Documents; its lines and candidates appear here.</p>
        </div>
      ) : (
        statements.map((statement) => {
          const check = BALANCE_CHECK[statement.balanceCheck] ?? { tone: 'warn', label: statement.balanceCheck, detail: '' };
          const sign = (amount: string) => `${statement.currency} ${money.forDisplay(amount)}`;
          return (
            <div key={statement.id} data-testid={`statement-${statement.reference}`}>
              <div className="card-body between" style={{ paddingBottom: 6 }}>
                <div>
                  <b>Statement {statement.reference}</b>
                  <p className="muted small">{statement.accountIban ?? statement.account}</p>
                </div>
                <span className={`badge ${check.tone}`} title={check.detail}>
                  {check.tone === 'ok' ? <Icon name="check" size={12} /> : null}
                  {check.label}
                </span>
              </div>
              <div className="row">
                <div className="grow"><strong>Opening balance</strong><p>{format.date(statement.opening.date)}</p></div>
                <b>{sign(statement.opening.amount)}</b>
              </div>
              {statement.lines.map((line) => (
                <div className="row" key={line.id}>
                  <div className="grow">
                    <strong>{line.counterparty ?? 'Counterparty not named'}</strong>
                    <p>
                      {line.reference ?? 'No description'} ·{' '}
                      {line.candidates === 0
                        ? 'no candidate invoice'
                        : `${line.candidates} ${line.candidates === 1 ? 'candidate' : 'candidates'}`}
                    </p>
                  </div>
                  <b>{sign(line.amount)}</b>
                </div>
              ))}
              {statement.storedLineCount < statement.lineCount ? (
                <p className="card-body muted small" style={{ paddingTop: 6, paddingBottom: 6 }}>
                  {statement.lineCount - statement.storedLineCount} of its {statement.lineCount} lines are stored with an
                  earlier statement.
                </p>
              ) : null}
              <div className="row">
                <div className="grow">
                  <strong>Closing balance</strong>
                  <p>
                    {money.forDisplay(statement.opening.amount)} {money.isNegative(statement.linesTotal) ? '−' : '+'}{' '}
                    {money.forDisplay(money.absolute(statement.linesTotal))} = {money.forDisplay(statement.closing.amount)}
                  </p>
                </div>
                <b>{sign(statement.closing.amount)}</b>
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}

function WeeksCard({
  read,
}: {
  readonly read: { data: BankMatchingFigures | null; error: string | null; loading: boolean };
}): ReactNode {
  const weeks = read.data?.weeks ?? [];
  const nothing = weeks.every((week) => week.offered === 0 && week.approved === 0);
  return (
    <section className="card" aria-labelledby="weeks-title">
      <div className="card-head">
        <h2 id="weeks-title">Matches per week</h2>
        <span className="muted small">last {weeks.length || 6} weeks</span>
      </div>
      {read.data === null ? (
        read.error === null ? (
          <div className="empty"><p>Reading…</p></div>
        ) : (
          <p className="card-body muted small" role="status">{read.error}</p>
        )
      ) : nothing ? (
        <div className="empty">
          <h3>No matches yet</h3>
          <p>No candidate was offered or approved in these weeks.</p>
        </div>
      ) : (
        <div className="card-body">
          <div className="legend">
            <span><span className="sq" style={{ background: CHART_COLOURS.grey }} />Offered</span>
            <span><span className="sq" style={{ background: CHART_COLOURS.green }} />Approved</span>
          </div>
          <BarChart
            caption="Bank matches per week: offered and approved"
            labels={weeks.map((week) => weekLabel(week.weekStart))}
            series={[
              { label: 'Offered', colour: CHART_COLOURS.grey, values: weeks.map((week) => week.offered) },
              { label: 'Approved', colour: CHART_COLOURS.green, values: weeks.map((week) => week.approved) },
            ]}
          />
        </div>
      )}
    </section>
  );
}

function MatchDetail({
  match,
  administration,
  constraints,
  contested,
  workspace,
  onChanged,
  onAskForDocument,
}: {
  readonly match: BankMatch;
  readonly administration: Administration;
  readonly constraints: BankMatches['constraints'];
  readonly contested: ReadonlySet<string>;
  readonly workspace: Workspace;
  readonly onChanged: () => void;
  readonly onAskForDocument?: ((subject: AskSubject) => void) | undefined;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [watchedOperationId, setWatchedOperationId] = useState<string | null>(null);

  const tenantId = workspace.membership.tenantId;
  const { token } = workspace;
  const blocked = blockedReason(match, constraints, workspace.membership.role);
  const residual = difference(match);

  const approve = useCallback(async () => {
    setBusy(true);
    setError(null);
    const idempotencyKey = newIdempotencyKey();
    try {
      const accepted = await request<Accepted>(
        tenantPath(tenantId, `/bank-matches/${encodeURIComponent(match.id)}/approve`),
        {
          method: 'POST',
          token,
          idempotencyKey,
          body: {
            // The staleness check. The version this screen DISPLAYED, so a bank
            // line that moved between the read and the click is refused by the
            // server rather than approved against a value nobody saw.
            expectedBankLineVersion: match.bankLine.currentVersion,
            decision: 'approve_match',
          },
        },
      );
      setWatchedOperationId(accepted.operationId);
      onChanged();
    } catch (caught) {
      setError(approvalMessage(caught));
      if (caught instanceof ApiError && caught.isStale) onChanged();
    } finally {
      setBusy(false);
    }
  }, [match, tenantId, token, onChanged]);

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>Review bank match</h2>
            <p>
              {administration.name}
              {administration.connection === null ? '' : ` · ${PROVIDER_NAMES[administration.connection.provider] ?? administration.connection.provider}`}
            </p>
          </div>
          <StateBadge table={MATCH_STATES} state={match.status} />
        </div>

        <div className="card-body">
          <div className="detail-grid">
            <section className="card">
              <div className="card-body">
                <small className="muted">BANK TRANSACTION · {format.date(match.bankLine.bookedAt)}</small>
                <h2 style={{ margin: '12px 0 6px' }}>
                  {match.bankLine.reference ?? match.bankLine.externalId}
                </h2>
                <div style={{ fontSize: 28, fontWeight: 600 }}>{money.format(match.bankLine.amount)}</div>
                <dl className="kv" style={{ marginTop: 14 }}>
                  <dt>Still unallocated</dt>
                  <dd>{money.format(match.bankLine.remaining)}</dd>
                  <dt>State</dt>
                  <dd>{match.bankLine.state.replace('_', ' ')}</dd>
                  <dt>Version</dt>
                  <dd>
                    <VersionPair
                      observed={match.bankLine.observedVersion}
                      current={match.bankLine.currentVersion}
                    />
                  </dd>
                </dl>
              </div>
            </section>

            <section className="card">
              <div className="card-body">
                <small className="muted">
                  {match.allocations.length === 1
                    ? 'RELATED INVOICE'
                    : `${match.allocations.length} RELATED INVOICES`}
                </small>
                {match.allocations.length === 0 ? (
                  <p className="muted small" style={{ marginTop: 12 }}>
                    This candidate has no allocations, so it would settle nothing.
                  </p>
                ) : (
                  match.allocations.map((allocation) => (
                    <div key={allocation.id} style={{ marginTop: 12 }}>
                      <h2 style={{ margin: '0 0 6px' }}>{allocation.invoiceNumber}</h2>
                      <div style={{ fontSize: 28, fontWeight: 600 }}>{money.format(allocation.amount)}</div>
                      <dl className="kv" style={{ marginTop: 14 }}>
                        <dt>Outstanding</dt>
                        <dd>{money.format(allocation.outstanding)}</dd>
                        <dt>Direction</dt>
                        <dd>{allocation.direction}</dd>
                        <dt>Version</dt>
                        <dd>
                          <VersionPair
                            observed={allocation.observedVersion}
                            current={allocation.currentVersion}
                          />
                        </dd>
                      </dl>
                      {contested.has(allocation.openItemId) ? (
                        <div className="callout warn" style={{ marginTop: 12 }}>
                          <b>Another candidate would settle this invoice too.</b> Amount alone does
                          not identify an invoice — check the reference before approving either.
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          <div
            className={`difference ${residual !== undefined && money.isZero(residual.amount) ? 'zero' : 'open'}`}
            data-testid="difference"
          >
            <span className="muted small">Difference</span>
            <b className="dn">
              {residual === undefined
                ? 'Not comparable — the amounts are in different currencies'
                : money.format(residual)}
            </b>
          </div>
          {residual !== undefined && !money.isZero(residual.amount) ? (
            <div className="callout warn" style={{ marginTop: 12 }}>
              The transaction and the invoices do not come to the same amount. Bank fees, discounts
              and part payments stay in review until they are explicitly covered.
            </div>
          ) : null}

          {match.bankLine.counterpartyName === null && match.bankLine.counterpartyIban === null ? null : (
            <p className="small" style={{ marginTop: 12 }}>
              Counterparty on the statement: {match.bankLine.counterpartyName ?? 'not named'}
              {match.bankLine.counterpartyIban === null ? null : <code style={{ fontSize: 11 }}> {match.bankLine.counterpartyIban}</code>}
            </p>
          )}

          <MatchReasons match={match} residualZero={residual !== undefined && money.isZero(residual.amount)} contested={match.allocations.some((allocation) => contested.has(allocation.openItemId))} />

          {match.confidence === null ? null : (
            <p className="muted small" style={{ marginTop: 12 }}>
              {/* Printed as the number it is. ch.02 allows AI to interpret
                  descriptions "only as a suggestion", so this is evidence about
                  how the candidate was found, not a verdict about whether it is
                  right. */}
              Matcher confidence {money.forDisplay(match.confidence)} — a score for how this
              candidate was found, not a judgement that it is correct.
            </p>
          )}

          <p className="muted small" style={{ marginTop: 12 }}>
            Reconciliation links a payment the bank already made to the invoice it settles. No
            payment is initiated.
          </p>
        </div>

        <div className="card-foot">
          <Constraints constraints={constraints} />
          {error === null ? null : (
            <p className="error-banner" role="alert" style={{ marginTop: 12 }}>
              {error}
            </p>
          )}
          <div className="between" style={{ marginTop: 12 }}>
            <span className="muted small">
              {blocked ?? 'Approving records your decision and queues one operation.'}
            </span>
            <div className="flex">
              {onAskForDocument === undefined ? null : (
                // Offered whatever the candidate's state is, and deliberately:
                // the reason to ask a customer for paperwork is usually that
                // the evidence here is not good enough to approve, so a control
                // that appeared only when everything was fine would be
                // available exactly when nobody needs it.
                <button
                  type="button"
                  className="btn"
                  data-testid="ask-for-document"
                  onClick={() =>
                    onAskForDocument({
                      kind: 'bank_line',
                      id: match.bankLine.id,
                      administrationId: administration.id,
                      label: `${match.bankLine.reference ?? match.bankLine.externalId} · ${money.format(match.bankLine.amount)}`,
                    })
                  }
                >
                  Ask the customer for the document
                </button>
              )}
              <button
                type="button"
                className="btn primary"
                disabled={blocked !== null || busy}
                onClick={() => void approve()}
                title={blocked ?? undefined}
              >
                {busy ? 'Submitting…' : 'Approve match'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ExternalState
        operations={[]}
        watchedOperationId={watchedOperationId}
        tenantId={tenantId}
        token={token}
        stillRunningHint="Still not settled. The operation keeps running in the background — reopen this candidate to see where it got to."
        onSettled={onChanged}
      />
    </div>
  );
}

/**
 * The version as observed when the candidate was computed, beside the version
 * stored now.
 *
 * Both, always, rather than a tick when they agree: ch.02 requires source
 * versions to be re-fetched before execution, and a reviewer who can see what
 * was compared can tell the difference between "checked and equal" and "not
 * checked". The endpoint reports both because it can only compare two STORED
 * values — neither is a statement about the provider right now.
 */
function VersionPair({ observed, current }: { readonly observed: string; readonly current: string }): ReactNode {
  const same = observed === current;
  return (
    <span>
      <code style={{ fontSize: 11 }}>{observed}</code>
      {' → '}
      <code style={{ fontSize: 11 }}>{current}</code>{' '}
      {same ? (
        <span className="muted small">unchanged</span>
      ) : (
        <span className="badge warn">changed</span>
      )}
    </span>
  );
}

function Constraints({ constraints }: { readonly constraints: BankMatches['constraints'] }): ReactNode {
  return (
    <dl className="kv">
      <dt>Administration</dt>
      <dd>{constraints.paused ? 'Paused — no new work may be queued' : 'Active'}</dd>
      <dt>Connection</dt>
      <dd>
        {constraints.connectionHealth === null
          ? 'Not visible to your session'
          : constraints.connectionHealth.replace('_', ' ')}
        {constraints.connectionHealth === 'reauthorisation_required' ||
        constraints.connectionHealth === 'disabled' ? (
          <>
            {' — '}
            <span className="muted">
              an approval can be queued but cannot be sent until the connection is fixed
            </span>
          </>
        ) : null}
      </dd>
      <dt>Automatic release</dt>
      {/* ch.02: "No broad 'AI can do everything' switch." An administration that
          has never had a release reports false, and false must not read as
          "something is switched off that should be on".
          
          TRUE must not overclaim either, and that is what changed when the
          release gates were built. A release exists and nothing consults it:
          no execution path in this system approves a match on a released
          policy, so "in force" would be the screen promising something the
          worker does not do. It says what is actually true of both halves. */}
      <dd>
        {constraints.automaticReleaseInForce
          ? 'Released for exact bank matching — and every match still needs your approval, because nothing acts on a release yet'
          : 'Not released — every match needs your approval'}
      </dd>
    </dl>
  );
}

/**
 * Why this candidate was offered, one line per reason, each from evidence the
 * screen already holds: the difference it computed, the matcher's stored
 * basis, and the contested set. A reason that does not hold is shown as a
 * warning, never dropped — the list must not read better than the evidence.
 */
function MatchReasons({
  match,
  residualZero,
  contested,
}: {
  readonly match: BankMatch;
  readonly residualZero: boolean;
  readonly contested: boolean;
}): ReactNode {
  const reasons: { ok: boolean; text: string }[] = [
    residualZero
      ? { ok: true, text: `The amount is exactly the same: ${money.format({ amount: money.absolute(match.bankLine.amount.amount), currency: match.bankLine.amount.currency })}` }
      : { ok: false, text: 'The amounts are not the same; see the difference above.' },
  ];
  if (match.basis === null) {
    reasons.push({ ok: false, text: 'This candidate predates the matcher, so how it was found is not recorded.' });
  } else {
    reasons.push(
      match.basis.kind === 'amount_and_invoice_number'
        ? { ok: true, text: 'The payment description names this invoice number' }
        : { ok: false, text: 'Found on the amount alone: the payment description does not name this invoice' },
    );
    reasons.push(
      match.basis.ambiguous
        ? { ok: false, text: `${match.basis.alternatives} invoices fit this payment equally well; each is offered separately and none is preferred` }
        : { ok: true, text: 'No other open invoice fits this payment as well' },
    );
  }
  if (contested) reasons.push({ ok: false, text: 'Another candidate would settle the same invoice' });
  return (
    <ul className="reasons" data-testid="match-reasons">
      {reasons.map((reason) => (
        <li key={reason.text} className={reason.ok ? 'ok' : 'warn'}>
          <Icon name={reason.ok ? 'check' : 'alert'} size={14} />
          {reason.text}
        </li>
      ))}
    </ul>
  );
}

function approvalMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) return 'The request could not be completed.';
  if (caught.isStale) {
    return 'This changed since the screen was loaded, so nothing was submitted. The candidates have been re-read.';
  }
  if (caught.status === 403) return 'Your role does not allow this action.';
  if (caught.isAbsent) return 'This candidate is no longer available to your session.';
  if (caught.status === 422) {
    return `The request was refused: ${caught.fieldErrors.map((field) => field.field).join(', ') || 'validation failed'}.`;
  }
  return caught.message;
}
